import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import FoodMenu from "@/components/FoodMenu";
import InquiryFormDialog from "@/components/InquiryFormDialog";
import {
  ArrowUpRight,
  MapPin,
  ShieldCheck,
  Wifi,
  Utensils,
  WashingMachine,
  Droplets,
  GlassWater,
  Zap,
  Cookie,
  BatteryCharging,
  Bus,
  Phone,
  Mail,
  MessageCircle,
  Clock,
  MessageSquare,
  Tag,
} from "lucide-react";
import RechargeDialog from "@/components/RechargeDialog";

const LOGO_URL =
  "https://customer-assets-rejwkqb3.emergentagent.net/job_student-lodging-10/artifacts/ajwvtlfr_f8761343-7645-4d67-a513-d9697831aa50.jpeg";

const AMENITIES = [
  "HIGH-SPEED WIFI",
  "WASHING MACHINE",
  "COOL & HOT WATER",
  "24/7 MINERAL WATER",
  "24/7 ELECTRICITY",
  "LIFT + CCTV",
  "VEG & NON-VEG FOOD",
  "SNACKS",
  "EV CHARGING",
  "TRANSPORTATION",
];

const FALLBACK_IMAGES = [
  "https://images.unsplash.com/photo-1624204386084-dd8c05e32226?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBidWlsZGluZyUyMGZhY2FkZSUyMHN1bnNldHxlbnwwfHx8fDE3ODUwODU1NjR8MA&ixlib=rb-4.1.0&q=85",
  "https://images.pexels.com/photos/4907235/pexels-photo-4907235.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "https://images.unsplash.com/photo-1768289269971-6171457bed13?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBob3N0ZWwlMjByb29tfGVufDB8fHx8MTc4NTA4NTU2NHww&ixlib=rb-4.1.0&q=85",
  "https://images.pexels.com/photos/36729772/pexels-photo-36729772.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
];

export default function Landing() {
  const [gallery, setGallery] = useState([]);
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [rechargeOpen, setRechargeOpen] = useState(false);

  useEffect(() => {
    api.get("/gallery").then((r) => setGallery(r.data || [])).catch(() => {});
  }, []);

  const galleryImages =
    gallery.length > 0
      ? gallery.map((g) => ({ src: fileUrl(g.photo_path), caption: g.caption }))
      : FALLBACK_IMAGES.map((src) => ({ src, caption: "" }));

  return (
    <div className="min-h-screen bg-[#F8F9FA] text-[#0A0A0A]" data-testid="landing-page">
      {/* Nav */}
      <nav
        className="sticky top-0 z-50 bg-[#FFE600] border-b-2 border-[#0A0A0A]"
        data-testid="landing-nav"
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-3">
          <Link to="/" className="flex items-center gap-3">
            <img
              src={LOGO_URL}
              alt="Vasavi Boys Hostel"
              className="h-10 w-auto border-2 border-[#0A0A0A] rounded-md bg-white object-contain"
            />
            <span className="hidden sm:block font-heading font-black text-lg tracking-tight leading-none">
              VASAVI
              <span className="block text-[10px] font-bold tracking-[0.3em] mt-1">BOYS HOSTEL</span>
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <a
              href="#contact"
              data-testid="nav-contact-link"
              className="hidden md:inline-flex items-center gap-2 font-bold text-sm px-4 py-2 border-2 border-[#0A0A0A] rounded-full bg-white hover:shadow-brutal-sm transition-shadow"
            >
              <Phone size={14} strokeWidth={2.5} /> Contact
            </a>
            <Link
              to="/login"
              data-testid="nav-login-link"
              className="hidden sm:inline-flex items-center gap-2 font-bold text-sm px-4 py-2 border-2 border-[#0A0A0A] rounded-full bg-white hover:shadow-brutal-sm transition-shadow"
            >
              Log in
            </Link>
            <Link
              to="/register"
              data-testid="nav-register-link"
              className="inline-flex items-center gap-2 font-bold text-sm px-5 py-2.5 border-2 border-[#0A0A0A] rounded-full bg-[#FF4D4D] text-white shadow-brutal hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-transform"
            >
              Book a Room <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </nav>

      {/* Rooms + Discount Banner (right-to-left marquee) */}
      <div
        className="bg-[#FF4D4D] text-white border-b-2 border-[#0A0A0A] overflow-hidden"
        data-testid="rooms-discount-banner"
      >
        <div className="marquee-wrap py-3">
          <div className="marquee-track font-heading font-black text-base sm:text-lg tracking-tight uppercase">
            {Array.from({ length: 4 }).map((_, i) => (
              <span key={i} className="inline-flex items-center gap-8 mx-8">
                <span className="inline-flex items-center gap-2">
                  <Tag size={16} strokeWidth={3} />
                  2, 3 &amp; 4 Sharing Rooms Available
                </span>
                <span>★</span>
                <span className="inline-flex items-center gap-2">
                  Flat 5% Discount on 1-Year Full Payment · T&amp;C Apply
                </span>
                <span>★</span>
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Hero with Logo Centerpiece */}
      <section className="relative overflow-hidden dot-bg">
        <div className="max-w-6xl mx-auto px-6 pt-16 pb-10 lg:pt-20 lg:pb-12 text-center">
          <span
            className="inline-block bg-[#00E5FF] border-2 border-[#0A0A0A] px-3 py-1 text-xs font-bold uppercase tracking-[0.2em] rounded-full mb-8"
            data-testid="hero-tagline"
          >
            Est. Since 2018 · Boys Hostel
          </span>

          {/* LOGO CENTERPIECE */}
          <div className="relative inline-block group" data-testid="hero-logo">
            <div className="absolute -inset-3 bg-[#FFE600] border-2 border-[#0A0A0A] rounded-2xl -rotate-2 -z-10"></div>
            <div className="absolute -inset-3 bg-[#00E5FF] border-2 border-[#0A0A0A] rounded-2xl rotate-2 -z-20"></div>
            <div className="relative bg-white border-2 border-[#0A0A0A] rounded-2xl shadow-brutal p-6 md:p-10">
              <img
                src={LOGO_URL}
                alt="Vasavi Boys Hostel — Feel Like Living Luxury"
                className="max-w-full h-auto mx-auto md:max-h-[340px] object-contain"
              />
            </div>
            <span className="absolute -bottom-5 left-1/2 -translate-x-1/2 whitespace-nowrap bg-[#FF4D4D] text-white font-heading font-black text-sm md:text-base px-5 py-2 border-2 border-[#0A0A0A] rounded-full shadow-brutal-sm">
              FEEL LIKE LIVING LUXURY
            </span>
          </div>

          <h1
            className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tighter mt-16 max-w-4xl mx-auto"
            data-testid="hero-headline"
          >
            A hostel that feels like{" "}
            <span className="bg-[#FFE600] px-3 border-2 border-[#0A0A0A]">home</span> —
            <span className="italic underline decoration-[#FF4D4D] decoration-4"> lives like a hotel.</span>
          </h1>
          <p className="text-lg sm:text-xl max-w-2xl mx-auto text-[#4A4A4A] font-medium mt-6">
            Vasavi Boys Hostel is where students settle in, focus up, and actually enjoy staying.
            Clean rooms, home-style meals, and everything sorted so you never have to think about it.
          </p>

          <div className="flex flex-wrap gap-4 justify-center mt-8">
            <Link
              to="/register"
              data-testid="hero-cta-primary"
              className="inline-flex items-center gap-2 font-bold px-6 py-3 border-2 border-[#0A0A0A] rounded-full bg-[#FFE600] shadow-brutal hover:translate-x-[3px] hover:translate-y-[3px] hover:shadow-none transition-transform"
            >
              Reserve a Room <ArrowUpRight size={18} />
            </Link>
            <a
              href="#gallery"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-2 font-bold px-6 py-3 border-2 border-[#0A0A0A] rounded-full bg-white shadow-brutal-sm hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-transform"
            >
              Peek Inside
            </a>
            <button
              onClick={() => setRechargeOpen(true)}
              data-testid="hero-cta-recharge"
              className="inline-flex items-center gap-2 font-bold px-6 py-3 border-2 border-[#0A0A0A] rounded-full bg-[#00CC66] text-white shadow-brutal-sm hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-transform"
            >
              <Zap size={18} strokeWidth={2.5} /> Recharge
            </button>
          </div>

          <div className="flex items-center gap-8 pt-10 justify-center text-sm font-bold uppercase tracking-widest text-[#4A4A4A]">
            <a
              href="https://maps.app.goo.gl/VJMdkqEtTzHDMK7W8"
              target="_blank"
              rel="noreferrer"
              data-testid="pg-location-link"
              className="flex items-center gap-2 font-bold uppercase tracking-widest hover:underline underline-offset-4 decoration-2 decoration-[#FF4D4D]"
            >
              <MapPin size={16} /> PG location
            </a>
            <span className="flex items-center gap-2"><ShieldCheck size={16} /> Verified & Safe</span>
          </div>
        </div>
      </section>

      {/* Marquee */}
      <div className="bg-[#0A0A0A] text-[#FFE600] border-y-2 border-[#0A0A0A] overflow-hidden">
        <div className="marquee-wrap py-4">
          <div className="marquee-track font-heading font-black text-xl tracking-tight">
            {[...AMENITIES, ...AMENITIES].map((a, i) => (
              <span key={i} className="mx-8">★ {a}</span>
            ))}
          </div>
        </div>
      </div>

      {/* Amenities Bento */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="mb-10 max-w-2xl">
          <span className="text-xs font-bold uppercase tracking-[0.2em]">What you get</span>
          <h2 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter mt-2">
            Everything you need. Under one roof.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6" data-testid="amenities-bento">
          <AmenityCard span={5} bg="#FFE600" fg="#0A0A0A" icon={Wifi} title="High-Speed Wi-Fi"
            desc="Fibre broadband across every room, terrace, and lounge — built for late-night deadlines." />
          <AmenityCard span={4} bg="#FFFFFF" fg="#0A0A0A" icon={WashingMachine} title="Washing Machine"
            desc="On-site washing machines — recharge to use" />
          <AmenityCard span={3} bg="#00E5FF" fg="#0A0A0A" icon={Droplets} title="Cool & Hot Water"
            desc="Electric geysers + chillers, running 24×7." />

          <AmenityCard span={4} bg="#FF4D4D" fg="#FFFFFF" icon={GlassWater} title="24/7 Mineral Water"
            desc="RO + UV filtered drinking water dispensers on every floor. Refill anytime." />
          <AmenityCard span={4} bg="#0A0A0A" fg="#FFE600" icon={Zap} title="Power + Lift + CCTV"
            desc="24×7 electricity with full backup, working lift, and full-coverage CCTV monitoring." />
          <AmenityCard span={4} bg="#00CC66" fg="#FFFFFF" icon={Utensils} title="Veg & Non-Veg Food"
            desc="Rotating home-style menus. Hygienic kitchens." />

          <AmenityCard span={4} bg="#FFE600" fg="#0A0A0A" icon={Cookie} title="Evening Snacks"
            desc="Fresh evening snacks with tea/coffee — no rushing outside between classes." />
          <AmenityCard span={4} bg="#00E5FF" fg="#0A0A0A" icon={BatteryCharging} title="EV Charging Station"
            desc="On-premises EV charging point for e-bikes and scooters." />
          <AmenityCard span={4} bg="#FFFFFF" fg="#0A0A0A" icon={Bus} title="Transportation"
            desc="Shuttle to campus + easy access to public transport right at the doorstep." />

          <AmenityCard span={12} bg="#00CC66" fg="#FFFFFF" icon={Zap} title="Prepaid Metres for Electricity"
            desc="Recharge your own room meter — pay only for what you actually use. We don't charge for generator backup." />

          <div className="md:col-span-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6"
            style={{
              backgroundColor: "#FFFFFF",
              color: "#0A0A0A",
              border: "2px solid #0A0A0A",
              borderRadius: "0.75rem",
              boxShadow: "4px 4px 0px 0px rgba(10,10,10,1)",
              padding: "2rem",
            }}
          >
            <div>
              <h3 className="font-heading font-black text-2xl">
                Ready to make Vasavi{" "}
                <span
                  className="inline-block px-2 border-2 border-[#0A0A0A] rounded-md"
                  style={{ backgroundColor: "#FF4D4D", color: "#FFFFFF" }}
                >
                  your new address
                </span>
                ?
              </h3>
              <p className="mt-2 font-medium max-w-xl">
                Book a visit, tour the rooms, and reserve your spot — all in a single afternoon.
              </p>
            </div>
            <Link
              to="/register"
              data-testid="amenities-cta"
              className="whitespace-nowrap inline-flex items-center gap-2 font-bold px-5 py-3 border-2 border-[#0A0A0A] rounded-full bg-[#0A0A0A] text-white shadow-brutal-sm hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-transform"
            >
              Join now <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section id="gallery" className="max-w-7xl mx-auto px-6 py-16">
        <div className="mb-10 flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.2em]">Peek inside</span>
            <h2 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter mt-2">
              The place, unfiltered.
            </h2>
          </div>
          <p className="max-w-sm font-medium text-[#4A4A4A]">
            Real photos. No staging. What you see is exactly what you get on move-in day.
          </p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="landing-gallery">
          {galleryImages.slice(0, 8).map((g, i) => (
            <div
              key={i}
              className={`brutal-card overflow-hidden p-0 ${i % 3 === 0 ? "md:row-span-2" : ""}`}
            >
              <img
                src={g.src}
                alt={g.caption || `Vasavi ${i}`}
                className={`w-full object-cover ${i % 3 === 0 ? "h-[400px]" : "h-[190px]"}`}
              />
            </div>
          ))}
        </div>
      </section>

      {/* Food Menu */}
      <FoodMenu />

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="brutal-card bg-[#FFE600] p-10 md:p-16 grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="font-heading font-black text-4xl md:text-5xl tracking-tighter">
              Ready to move in?
            </h2>
          </div>
          <div className="flex flex-wrap gap-4 md:justify-end">
            <Link
              to="/register"
              data-testid="footer-cta-book"
              className="inline-flex items-center gap-2 font-bold px-6 py-3 border-2 border-[#0A0A0A] rounded-full bg-[#00CC66] text-white shadow-brutal hover:translate-x-[3px] hover:translate-y-[3px] hover:shadow-none transition-transform"
            >
              Book a room <ArrowUpRight size={18} />
            </Link>
            <Link
              to="/login"
              data-testid="footer-cta-login"
              className="inline-flex items-center gap-2 font-bold px-6 py-3 border-2 border-[#0A0A0A] rounded-full bg-white shadow-brutal-sm hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-transform"
            >
              Already a resident? Log in
            </Link>
          </div>
        </div>
      </section>

      {/* Contact Us — big, prominent, bottom of page */}
      <section id="contact" className="max-w-7xl mx-auto px-6 py-16" data-testid="contact-section">
        <div
          className="border-2 border-[#0A0A0A] rounded-2xl overflow-hidden"
          style={{ boxShadow: "6px 6px 0px 0px rgba(10,10,10,1)" }}
        >
          {/* Header */}
          <div className="p-8 md:p-12 border-b-2 border-[#0A0A0A]" style={{ backgroundColor: "#0A0A0A", color: "#FFE600" }}>
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
              <div>
                <span className="text-xs font-bold uppercase tracking-[0.3em]">Say hello</span>
                <h2 className="font-heading font-black text-5xl sm:text-6xl md:text-7xl tracking-tighter mt-2 leading-[0.9]">
                  CONTACT{" "}
                  <span
                    className="inline-block px-3 border-2 border-[#FFE600]"
                    style={{ backgroundColor: "#FFE600", color: "#0A0A0A" }}
                  >
                    US
                  </span>
                </h2>
                <p className="mt-4 max-w-2xl font-medium text-base sm:text-lg opacity-90">
                  Have questions about rooms, food, admission, or a hostel visit? Reach the wardens directly — we
                  respond within a few hours.
                </p>
              </div>
              <div className="flex flex-col gap-3 md:items-end">
                <span
                  data-testid="visit-hours"
                  className="inline-flex items-center gap-2 px-4 py-2 border-2 border-[#FFE600] rounded-full font-bold text-sm"
                >
                  <Clock size={16} strokeWidth={2.5} /> Visit us · 8 AM – 9 PM
                </span>
                <button
                  onClick={() => setInquiryOpen(true)}
                  data-testid="open-inquiry-form"
                  className="inline-flex items-center gap-2 font-bold px-5 py-2.5 border-2 border-[#FFE600] rounded-full bg-[#FFE600] text-[#0A0A0A] shadow-brutal-sm hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-transform"
                >
                  <MessageSquare size={16} strokeWidth={2.5} /> Send us a message
                </button>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6 md:p-10 bg-white">
            <ContactPersonCard
              name="Chandra Mouli"
              role="Warden"
              phone="9581547143"
              bg="#FFE600"
              fg="#0A0A0A"
              testId="contact-person-mouli"
            />
            <ContactPersonCard
              name="Aruna"
              role="Owner"
              phone="9490070783"
              bg="#00E5FF"
              fg="#0A0A0A"
              testId="contact-person-aruna"
            />

            <a
              href="mailto:info.vasaviboyshostel@gmail.com"
              data-testid="contact-email"
              className="block group"
              style={{
                backgroundColor: "#FF4D4D",
                color: "#FFFFFF",
                border: "2px solid #0A0A0A",
                borderRadius: "0.75rem",
                boxShadow: "4px 4px 0px 0px rgba(10,10,10,1)",
                padding: "1.75rem",
              }}
            >
              <div className="flex items-center justify-between">
                <Mail size={30} strokeWidth={2.5} />
                <ArrowUpRight size={22} strokeWidth={2.5} />
              </div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] mt-4">Email</div>
              <div className="font-heading font-black text-xl sm:text-2xl mt-1 break-all">
                info.vasaviboyshostel@gmail.com
              </div>
            </a>

            <a
              href="https://maps.app.goo.gl/VJMdkqEtTzHDMK7W8"
              target="_blank"
              rel="noreferrer"
              data-testid="contact-address"
              className="block group"
              style={{
                backgroundColor: "#00CC66",
                color: "#FFFFFF",
                border: "2px solid #0A0A0A",
                borderRadius: "0.75rem",
                boxShadow: "4px 4px 0px 0px rgba(10,10,10,1)",
                padding: "1.75rem",
              }}
            >
              <div className="flex items-center justify-between">
                <MapPin size={30} strokeWidth={2.5} />
                <ArrowUpRight size={22} strokeWidth={2.5} />
              </div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] mt-4">Address</div>
              <div className="font-heading font-black text-base sm:text-lg mt-1 leading-tight">
                Vasavi Boys Hostel, R.S Srigdha Residency,<br />
                Seshamsetty Street, opposite KL Women Technology Park,<br />
                Kollunakondha Village, Tadapalli Mandalam,<br />
                Guntur District, Andhra Pradesh — 522501
              </div>
            </a>
          </div>
        </div>
      </section>

      <footer className="border-t-2 border-[#0A0A0A] bg-white">
        <div className="max-w-7xl mx-auto px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <img
              src={LOGO_URL}
              alt="Vasavi Boys Hostel"
              className="h-9 w-auto border-2 border-[#0A0A0A] rounded-md bg-white"
            />
            <div className="font-heading font-black text-lg leading-none">
              VASAVI
              <div className="text-[10px] font-bold tracking-[0.3em] mt-0.5">BOYS HOSTEL</div>
            </div>
          </div>
        </div>
      </footer>
      <InquiryFormDialog open={inquiryOpen} onOpenChange={setInquiryOpen} />
      <RechargeDialog open={rechargeOpen} onOpenChange={setRechargeOpen} />
    </div>
  );
}

function AmenityCard({ span, bg, fg, icon: Icon, title, desc }) {
  return (
    <div
      style={{
        backgroundColor: bg,
        color: fg,
        border: "2px solid #0A0A0A",
        borderRadius: "0.75rem",
        boxShadow: "4px 4px 0px 0px rgba(10,10,10,1)",
        padding: "2rem",
        gridColumn: `span ${span} / span ${span}`,
      }}
    >
      <Icon size={36} strokeWidth={2.5} />
      <h3 className="font-heading font-black text-2xl mt-4">{title}</h3>
      <p className="mt-2 font-medium">{desc}</p>
    </div>
  );
}

function ContactPersonCard({ name, role, phone, bg, fg, testId }) {
  const waLink = `https://wa.me/91${phone}`;
  return (
    <div
      data-testid={testId}
      style={{
        backgroundColor: bg,
        color: fg,
        border: "2px solid #0A0A0A",
        borderRadius: "0.75rem",
        boxShadow: "4px 4px 0px 0px rgba(10,10,10,1)",
        padding: "1.75rem",
      }}
    >
      <div className="flex items-center justify-between">
        <div className="text-xs font-bold uppercase tracking-[0.2em] px-2 py-1 border-2 border-current rounded-full">
          {role}
        </div>
        <Phone size={26} strokeWidth={2.5} />
      </div>
      <h3 className="font-heading font-black text-3xl tracking-tighter mt-4">{name}</h3>
      <a
        href={`tel:+91${phone}`}
        data-testid={`${testId}-phone-link`}
        className="mt-2 inline-block font-heading font-black text-2xl tracking-tight hover:underline decoration-2 underline-offset-4"
      >
        +91 {phone.replace(/(\d{5})(\d{5})/, "$1 $2")}
      </a>
      <div className="mt-4 flex flex-wrap gap-2">
        <a
          href={`tel:+91${phone}`}
          data-testid={`${testId}-call-link`}
          className="inline-flex items-center gap-2 font-bold text-sm px-4 py-2 border-2 border-[#0A0A0A] rounded-full bg-white text-[#0A0A0A] hover:shadow-brutal-sm transition-shadow"
        >
          <Phone size={14} strokeWidth={2.5} /> Call
        </a>
        <a
          href={waLink}
          target="_blank"
          rel="noreferrer"
          data-testid={`${testId}-whatsapp-link`}
          className="inline-flex items-center gap-2 font-bold text-sm px-4 py-2 border-2 border-[#0A0A0A] rounded-full bg-[#0A0A0A] text-white hover:shadow-brutal-sm transition-shadow"
        >
          <MessageCircle size={14} strokeWidth={2.5} /> WhatsApp
        </a>
      </div>
    </div>
  );
}
